// components/AssignSlatesDialog.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    Grid,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
} from "@mui/material";
import {
    Search as SearchIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import * as AssignSlatesService from "../../services/AssignSlatesService";

const AssignSlatesDialog = ({
    open,
    onClose,
    mode = "SAMPLE", // "SAMPLE" | "QUOTE" | "SLATE" | "BOTTLEORDER"
    sampleNumber,
    quoteNumber,
    accountNumber,
    slateId,
    orderNumber,
    onAssignComplete,
}) => {
    // State management
    const [slates, setSlates] = useState([]);
    const [tests, setTests] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSlateId, setSelectedSlateId] = useState("");
    const [selectedSlateName, setSelectedSlateName] = useState("");
    const [selectedSlates, setSelectedSlates] = useState([]);
    const [selectedTests, setSelectedTests] = useState([]);
    const [selectAllTests, setSelectAllTests] = useState(true);
    const [loading, setLoading] = useState(false);
    const [testsLoading, setTestsLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("info");

    const searchInputRef = useRef(null);

    // Determine display modes - matches VB.NET conditional rendering
    const showSlateCheckboxes = mode === "QUOTE";
    const showTestCheckboxes = mode === "SAMPLE" || mode === "SLATE";

    // Load slates based on search query - Matches VB.NET SelectJobSlates
    const loadSlates = async (query) => {
        if (!query || query.trim().length === 0) {
            setSlates([]);
            return;
        }

        setLoading(true);
        try {
            const data = await AssignSlatesService.SearchSlates(query);
            setSlates(data.results || data || []);
            setAlertMessage("");
        } catch (error) {
            console.error("Error loading slates:", error);
            NotificationManager.error("Failed to load slates", "Error");
            setAlertMessage("Failed to load slates");
            setAlertSeverity("error");
        } finally {
            setLoading(false);
        }
    };

    // Load tests for selected slate - Matches VB.NET ShowAssignedTests
    const loadTests = async (jobId) => {
        if (!jobId) return;

        setTestsLoading(true);
        setTests([]);
        setAlertMessage("");

        try {
            const data = await AssignSlatesService.GetSlateTests(jobId);
            const testList = data.results || data || [];
            setTests(testList);

            // Select all tests by default (matches VB.NET behavior where all checkboxes are checked=true)
            if (testList.length > 0) {
                setSelectedTests(testList.map((t) => t.testCode));
                setSelectAllTests(true);
            } else {
                setSelectedTests([]);
                setSelectAllTests(false);
            }
        } catch (error) {
            console.error("Error loading tests:", error);
            NotificationManager.error("Failed to load tests", "Error");
            setAlertMessage("Failed to load tests");
            setAlertSeverity("error");
        } finally {
            setTestsLoading(false);
        }
    };

    // Handle slate selection - Matches VB.NET gvSlates_Command (lbJob_id click)
    const handleSlateClick = (slate) => {
        // Allow clicking slate to view tests even in QUOTE mode
        setSelectedSlateId(slate.jobId);
        setSelectedSlateName(slate.jobName);
        loadTests(slate.jobId);
        setAlertMessage("");
    };

    // Handle slate checkbox for quote mode - VB.NET cbSelectSlate
    const handleSlateCheckbox = (jobId, event) => {
        event?.stopPropagation(); // Prevent row click when clicking checkbox
        setSelectedSlates((prev) =>
            prev.includes(jobId)
                ? prev.filter((id) => id !== jobId)
                : [...prev, jobId]
        );

    };

    // Handle test selection - VB.NET cbSelect checkbox
    const handleTestCheckbox = (testCode) => {
        setSelectedTests((prev) => {
            const newSelection = prev.includes(testCode)
                ? prev.filter((code) => code !== testCode)
                : [...prev, testCode];

            // Update select all checkbox state
            setSelectAllTests(newSelection.length === tests.length && tests.length > 0);
            return newSelection;
        });
    };

    // Handle select all tests - Matches VB.NET CheckUncheckAll function
    const handleSelectAllTests = () => {
        if (selectAllTests) {
            setSelectedTests([]);
        } else {
            setSelectedTests(tests.map((t) => t.testCode));
        }
        setSelectAllTests(!selectAllTests);
    };

    // Assign tests to sample - Matches VB.NET AssignTests
    const assignTestsToSample = async () => {
        if (selectedTests.length === 0) {
            // Matches VB.NET: "You did not select any tests."
            setAlertMessage("You did not select any tests.");
            setAlertSeverity("warning");
            return;
        }

        if (!selectedSlateId) {
            setAlertMessage("Please select a slate first");
            setAlertSeverity("warning");
            return;
        }

        setLoading(true);
        try {
            const result = await AssignSlatesService.AssignTestsToSample(
                sampleNumber,
                selectedSlateId,
                selectedTests
            );

            // Let the parent save the tests first, so a failed save lands in the
            // catch below instead of showing as a success.
            if (onAssignComplete) {
                await onAssignComplete({
                    jobId: selectedSlateId,
                    testCodes: selectedTests,
                    assignedTests: result.assignedTests || result,
                });
            }

            // Matches VB.NET message format: "N tests assigned..."
            const count = selectedTests.length;
            const message = count === 1 ? "1 test assigned..." : `${count} tests assigned...`;
            setAlertMessage(message);
            setAlertSeverity("success");
            NotificationManager.success(message, "Success");

            // Clear selections after assignment (matches VB.NET: TestCheckbox.Checked = False)
            setSelectedTests([]);
            setSelectAllTests(false);
            return true;
        } catch (error) {
            console.error("Error assigning tests:", error);
            const errorMsg = error.message || "Failed to assign tests to sample";
            setAlertMessage(errorMsg);
            setAlertSeverity("error");
            NotificationManager.error(errorMsg, "Error");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Assign slates to quote - Matches VB.NET AssignSlatestoQuote
    const assignSlatesToQuote = async () => {
        if (selectedSlates.length === 0) {
            setAlertMessage("Please select at least one slate");
            setAlertSeverity("warning");
            return;
        }

        setLoading(true);
        try {
            const result = await AssignSlatesService.AssignSlatesToQuote(
                quoteNumber,
                accountNumber,
                selectedSlates
            );

            const count = selectedSlates.length;
            const message = `${count} slate${count === 1 ? '' : 's'} assigned to quote`;
            setAlertMessage(message);
            setAlertSeverity("success");
            NotificationManager.success(message, "Success");

            if (onAssignComplete) {
                onAssignComplete({
                    jobIds: selectedSlates,
                    transactions: result.transactions || result,
                });
            }

            // Clear selections
            setSelectedSlates([]);
        } catch (error) {
            console.error("Error assigning slates:", error);
            const errorMsg = "Failed to assign slates to quote";
            setAlertMessage(errorMsg);
            setAlertSeverity("error");
            NotificationManager.error(errorMsg, "Error");
        } finally {
            setLoading(false);
        }
    };

    // Assign tests to slate - Matches VB.NET AssignTeststoSlate
    const assignTestsToSlate = async () => {
        if (selectedTests.length === 0) {
            setAlertMessage("You did not select any tests.");
            setAlertSeverity("warning");
            return;
        }

        const jobIdToUse = slateId || selectedSlateId;
        if (!jobIdToUse) {
            setAlertMessage("No slate selected");
            setAlertSeverity("error");
            return;
        }

        setLoading(true);
        try {
            const result = await AssignSlatesService.AssignTestsToSlate(
                jobIdToUse,
                selectedTests
            );

            // Matches VB.NET message format: "N Tests Assigned to Slate JobId ...".
            // Use the count the API reports - tests already on the slate are skipped.
            const count = result?.assignedCount ?? selectedTests.length;
            const message =
                result?.message ??
                `${count} Test${count === 1 ? '' : 's'} Assigned to Slate ${jobIdToUse} ...`;
            setAlertMessage(message);
            setAlertSeverity("success");
            NotificationManager.success(message, "Success");

            // Uncheck assigned tests (matches VB.NET: cb.Checked = False)
            setSelectedTests([]);
            setSelectAllTests(false);

            if (onAssignComplete) {
                onAssignComplete({
                    jobId: jobIdToUse,
                    testCodes: selectedTests,
                    assignedCount: count,
                });
            }

            // Reload tests to show updated list
            await loadTests(jobIdToUse);
        } catch (error) {
            console.error("Error assigning tests to slate:", error);
            const errorMsg = "Failed to assign tests to slate";
            setAlertMessage(errorMsg);
            setAlertSeverity("error");
            NotificationManager.error(errorMsg, "Error");
        } finally {
            setLoading(false);
        }
    };

    // Assign slate to bottle order - Matches VB.NET AssignTeststoBottleOrder
    const assignSlateToBottleOrder = async () => {
        if (!selectedSlateId) {
            setAlertMessage("Please select a slate");
            setAlertSeverity("warning");
            return;
        }

        setLoading(true);
        try {
            const result = await AssignSlatesService.AssignSlateToBottleOrder(
                orderNumber,
                selectedSlateId
            );

            const message = `Slate ${selectedSlateId} assigned to bottle order`;
            setAlertMessage(message);
            setAlertSeverity("success");
            NotificationManager.success(message, "Success");

            if (onAssignComplete) {
                onAssignComplete({
                    jobId: selectedSlateId,
                    orderedBottle: result.orderedBottle || result,
                });
            }
        } catch (error) {
            console.error("Error assigning slate to bottle order:", error);
            const errorMsg = "Failed to assign slate to bottle order";
            setAlertMessage(errorMsg);
            setAlertSeverity("error");
            NotificationManager.error(errorMsg, "Error");
        } finally {
            setLoading(false);
        }
    };

    // Main assign handler - Matches VB.NET Page_Load postback handling with vPostbackcode
    const handleAssign = async (closeAfter = false) => {
        try {
            // assignTestsToSample reports its own outcome; alertSeverity is still
            // the pre-assign value here, so it can't be used to decide on closing.
            let succeeded = alertSeverity === "success";
            switch (mode) {
                case "SAMPLE":
                    succeeded = await assignTestsToSample();
                    break;
                case "QUOTE":
                    await assignSlatesToQuote();
                    break;
                case "SLATE":
                    await assignTestsToSlate();
                    break;
                case "BOTTLEORDER":
                    await assignSlateToBottleOrder();
                    break;
                default:
                    break;
            }

            // VB.NET behavior: closeAfter matches vPostbackcode = 1 (close) vs 2 (stay open)
            if (closeAfter && succeeded) {
                setTimeout(() => onClose(), 1500);
            }
        } catch (error) {
            console.error("Error in handleAssign:", error);
        }
    };

    // Search debounce - Matches VB.NET tbSlateQryArg_TextChanged with AutoPostBack
    useEffect(() => {
        const timer = setTimeout(() => {
            loadSlates(searchQuery);
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Reset on open - Matches VB.NET Page_Load Not IsPostBack initialization
    useEffect(() => {
        if (open) {
            // Reset all state
            setSearchQuery("");
            setSlates([]);
            setTests([]);
            setSelectedSlateId(slateId || "");
            setSelectedSlateName("");
            setSelectedSlates([]);
            setSelectedTests([]);
            setSelectAllTests(true);
            setAlertMessage("");
            setAlertSeverity("info");
            setLoading(false);
            setTestsLoading(false);

            // SLATE mode copies tests from another slate onto slateId, so the test list
            // starts empty and fills in once a source slate is picked. Pre-loading the
            // target's own tests here would offer the user its existing tests to re-add.
            if (mode !== "SLATE" && slateId) {
                loadTests(slateId);
            }
        }
    }, [open, mode, slateId]);

    // Focus on search input when dialog opens
    useEffect(() => {
        if (open && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [open]);

    const getTitle = () => {
        switch (mode) {
            case "SAMPLE":
                return `Assign Laboratory Test Slates - Sample ${sampleNumber}`;
            case "QUOTE":
                return `Assign Laboratory Test Slates - Quote ${quoteNumber}`;
            case "SLATE":
                return `Assign Laboratory Test Slates - Slate ${slateId || selectedSlateId}`;
            case "BOTTLEORDER":
                return `Assign Laboratory Test Slates - Bottle Order ${orderNumber}`;
            default:
                return "Assign Laboratory Test Slates";
        }
    };

    const getButtonText = (closeAfter) => {
        if (mode === "QUOTE") {
            return closeAfter ? "Assign Jobs and Exit" : "Assign Jobs";
        }
        return closeAfter ? "Assign Tests and Exit" : "Assign Tests";
    };

    const isAssignDisabled = () => {
        if (loading || testsLoading) return true;

        if (mode === "QUOTE") {
            return selectedSlates.length === 0;
        }
        if (mode === "BOTTLEORDER") {
            return !selectedSlateId;
        }
        // SAMPLE and SLATE modes
        return selectedTests.length === 0 || !selectedSlateId;
    };

    // Get display value for sample/quote/order number in footer
    const getDisplayNumber = () => {
        switch (mode) {
            case "SAMPLE": return sampleNumber;
            case "QUOTE": return quoteNumber;
            case "BOTTLEORDER": return orderNumber;
            case "SLATE": return slateId || selectedSlateId;
            default: return "N/A";
        }
    };

    const getDisplayLabel = () => {
        switch (mode) {
            case "SAMPLE": return "Sample:";
            case "QUOTE": return "Quote:";
            case "BOTTLEORDER": return "Order:";
            case "SLATE": return "Slate:";
            default: return "";
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: "90vw",
                    height: "90vh",
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    margin: "2.5vh auto",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            {/* Title matches VB.NET header background color #005FA9 */}
            <DialogTitle sx={{ bgcolor: "#005FA9", color: "white", p: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: "12pt", fontWeight: "bold" }}>
                    {getTitle()}
                </Typography>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden",
                }}
            >
                {/* Alert message - matches VB.NET lblAlert */}
                {alertMessage && (
                    <Alert severity={alertSeverity} sx={{ m: 2, mb: 0 }}>
                        {alertMessage}
                    </Alert>
                )}

                <Grid container sx={{ flexGrow: 1, height: "100%", overflow: "hidden" }}>
                    {/* Left Panel - Slates (matches VB.NET left table column) */}
                    <Grid
                        item
                        xs={5}
                        sx={{
                            borderRight: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        {/* Search box - matches VB.NET bgcolor="#ece9d8" */}
                        <Box sx={{ p: 2, bgcolor: "#ece9d8", borderBottom: "1px solid #ccc" }}>
                            <Typography variant="body2" sx={{ fontSize: "9pt", mb: 1 }}>
                                Test Slate Search:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by Job ID, Name, Client, Account, PWS..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                inputRef={searchInputRef}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, fontSize: "1.2rem" }} />,
                                    sx: { fontSize: "9pt" },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>

                        {/* Slates Grid - matches VB.NET gvSlates with scrollable div */}
                        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                            {loading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : slates.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: "center" }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "9pt", fontWeight: "bold" }}>
                                        {searchQuery.trim().length > 0
                                            ? "No Slates Found"
                                            : "Enter search criteria to find slates"}
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
                                        <TableHead>
                                            <TableRow>
                                                {/* Header matches VB.NET BackColor="#005FA9" ForeColor="White" */}
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "100px", // Fixed width for Slate ID
                                                        minWidth: "100px",
                                                    }}
                                                >
                                                    Slate ID
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "auto", // Flexible for Slate Name
                                                        minWidth: "150px",
                                                    }}
                                                >
                                                    Slate Name
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "200px", // Fixed width for Customers
                                                        minWidth: "200px",
                                                        maxWidth: "200px",
                                                    }}
                                                >
                                                    Customers
                                                </TableCell>
                                                {showSlateCheckboxes && (
                                                    <TableCell
                                                        sx={{
                                                            bgcolor: "#005FA9",
                                                            color: "white",
                                                            fontWeight: "bold",
                                                            width: "60px", // Fixed width for checkbox
                                                            minWidth: "60px",
                                                            maxWidth: "60px",
                                                            fontSize: "9pt",
                                                            padding: "4px 8px",
                                                        }}
                                                    >
                                                        Select
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {slates.map((slate, index) => (
                                                <TableRow
                                                    key={slate.jobId}
                                                    sx={{
                                                        bgcolor: index % 2 === 1 ? "ghostwhite" : "white",
                                                        cursor: "pointer",
                                                        "&:hover": { bgcolor: "#e3f2fd" },
                                                        ...(selectedSlateId === slate.jobId && {
                                                            bgcolor: "#bbdefb",
                                                        }),
                                                    }}
                                                    onClick={() => handleSlateClick(slate)}
                                                >
                                                    <TableCell sx={{
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "100px",
                                                        minWidth: "100px",
                                                    }}>
                                                        <Typography
                                                            variant="body2"
                                                            color="primary"
                                                            sx={{ fontWeight: 500, fontSize: "9pt" }}
                                                        >
                                                            {slate.jobId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            bgcolor: "lightcyan",
                                                            fontSize: "9pt",
                                                            padding: "4px 8px",
                                                            width: "auto",
                                                            minWidth: "150px",
                                                        }}
                                                    >
                                                        {slate.jobName}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            bgcolor: slate.customers === "ALL" ? "bisque" : "white",
                                                            fontSize: "9pt",
                                                            padding: "4px 8px",
                                                            width: "200px",
                                                            minWidth: "200px",
                                                            maxWidth: "200px",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                        title={slate.customers}
                                                    >
                                                        {slate.customers}
                                                    </TableCell>
                                                    {showSlateCheckboxes && (
                                                        <TableCell sx={{
                                                            padding: "4px 8px",
                                                            width: "60px",
                                                            minWidth: "60px",
                                                            maxWidth: "60px",
                                                        }}>
                                                            <Checkbox
                                                                checked={selectedSlates.includes(slate.jobId)}
                                                                onChange={(e) => handleSlateCheckbox(slate.jobId, e)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>

                        {/* Selected slate info - matches VB.NET bottom row bgcolor="gainsboro" */}
                        {selectedSlateId && !showSlateCheckboxes && (
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: "gainsboro",
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: "bold", fontSize: "9pt" }}
                                >
                                    Job ID:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ ml: 1, fontSize: "9pt" }}
                                >
                                    {selectedSlateId}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ ml: 1, fontSize: "9pt" }}
                                >
                                    {selectedSlateName}
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* Right Panel - Tests (matches VB.NET right table column) */}
                    <Grid
                        item
                        xs={7}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        {/* Header - matches VB.NET bgcolor="#ece9d8" */}
                        <Box sx={{ p: 2, bgcolor: "#ece9d8", borderBottom: "1px solid #ccc" }}>
                            <Typography variant="body2" sx={{ fontSize: "9pt", fontWeight: "bold" }}>
                                Laboratory Tests
                            </Typography>
                        </Box>

                        {/* Tests Grid - matches VB.NET gvTests */}
                        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                            {testsLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : tests.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: "center" }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: "9pt", fontWeight: "bold" }}
                                    >
                                        {selectedSlateId
                                            ? "No test records found"
                                            : "Select a slate to view tests"}
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "120px",
                                                        minWidth: "120px",
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    Test Code
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "auto",
                                                        minWidth: "200px",
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    Parameter
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "100px",
                                                        minWidth: "100px",
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    Units
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "9pt",
                                                        padding: "4px 8px",
                                                        width: "120px",
                                                        minWidth: "120px",
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    Method
                                                </TableCell>
                                                {showTestCheckboxes && (
                                                    <TableCell
                                                        sx={{
                                                            bgcolor: "#005FA9",
                                                            color: "white",
                                                            fontWeight: "bold",
                                                            width: "60px",
                                                            minWidth: "60px",
                                                            maxWidth: "60px",
                                                            fontSize: "9pt",
                                                            padding: "4px 8px",
                                                            textAlign: "center",
                                                            position: "sticky",
                                                            top: 0,
                                                            zIndex: 2,
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={selectAllTests}
                                                            onChange={handleSelectAllTests}
                                                            sx={{ color: "white", padding: 0 }}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tests.map((test, index) => (
                                                <TableRow
                                                    key={test.testCode}
                                                    sx={{
                                                        bgcolor: index % 2 === 1 ? "ghostwhite" : "white",
                                                    }}
                                                >
                                                    <TableCell sx={{ fontSize: "9pt", padding: "4px 8px" }}>
                                                        {test.testCode}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "9pt", padding: "4px 8px" }}>
                                                        {test.parameter}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "9pt", padding: "4px 8px" }}>
                                                        {test.testUnits}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "9pt", padding: "4px 8px" }}>
                                                        {test.methodCode}
                                                    </TableCell>
                                                    {showTestCheckboxes && (
                                                        <TableCell sx={{ padding: "4px 8px", textAlign: "center" }}>
                                                            {/* VB.NET cbSelect with Checked="True" by default */}
                                                            <Checkbox
                                                                checked={selectedTests.includes(test.testCode)}
                                                                onChange={() => handleTestCheckbox(test.testCode)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Footer - matches VB.NET bottom row bgcolor="gainsboro" */}
            <DialogActions
                sx={{
                    px: 3,
                    py: 1.5,
                    bgcolor: "gainsboro",
                    justifyContent: "space-between",
                    borderTop: "1px solid #ccc",
                }}
            >
                <Box>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: "bold", fontSize: "9pt" }}
                    >
                        {getDisplayLabel()}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ ml: 1, fontSize: "9pt" }}
                    >
                        {getDisplayNumber()}
                    </Typography>
                </Box>
                <Box>
                    <Button
                        onClick={onClose}
                        sx={{ mr: 1, fontSize: "9pt" }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    {/* VB.NET cmdAssign - stays open after assign */}
                    <Button
                        variant="contained"
                        onClick={() => handleAssign(false)}
                        disabled={isAssignDisabled()}
                        sx={{ mr: 1, fontSize: "9pt" }}
                    >
                        {getButtonText(false)}
                    </Button>
                    {/* VB.NET cmdAssignandExit - closes after assign */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAssign(true)}
                        disabled={isAssignDisabled()}
                        sx={{ fontSize: "9pt" }}
                    >
                        {getButtonText(true)}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default AssignSlatesDialog;